import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodSchema, oz } from '@vy/utils';
import { useImperativeHandle, useRef, type FC } from 'react';
import { forwardRef } from 'react';
import { useForm, FormProvider, type Control } from 'react-hook-form';
import { type IntegrationDrawerRef, IntegrationDrawer } from '../components/IntegrationDrawer';

type ComponentProps<Schema extends ZodSchema = ZodSchema> = {
  control: Control<oz.infer<Schema>>;
};

export type FormDrawerProps<Schema extends ZodSchema = ZodSchema> = {
  zodSchema?: Schema;
  Footer?: FC<ComponentProps<Schema>>;
  ContentComponent?: FC<ComponentProps<Schema>>;
};

interface Props<T extends ZodSchema = ZodSchema> extends FormDrawerProps<T> {
  onClose: () => void;
}

export const FormDrawer = forwardRef<IntegrationDrawerRef, Props>(
  ({ ContentComponent, zodSchema, Footer, onClose }, ref) => {
    const formMethods = useForm({
      resolver: zodSchema ? zodResolver(zodSchema) : undefined,
    });
    const { control, reset } = formMethods;

    const drawerRef = useRef<IntegrationDrawerRef>(null);
    useImperativeHandle(ref, () => ({
      openDrawer: () => drawerRef.current?.openDrawer(),
      closeDrawer: () => drawerRef.current?.closeDrawer(),
    }));

    return (
      <FormProvider {...formMethods}>
        <IntegrationDrawer
          ref={drawerRef}
          anchor="right"
          onClose={() => {
            onClose?.();
            reset();
          }}
          footer={Footer && <Footer control={control} />}
        >
          {ContentComponent && <ContentComponent control={control} />}
        </IntegrationDrawer>
      </FormProvider>
    );
  },
);
